import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth, useLang, API } from '../../App';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { TimerReset, LogOut } from 'lucide-react';

const IDLE_MS = 30 * 60 * 1000;
const WARN_MS = 2 * 60 * 1000;
const HEARTBEAT_MS = 10 * 60 * 1000;
const EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'wheel'];

export default function IdleSessionGuard() {
  const { logout } = useAuth();
  const { lang } = useLang();
  const navigate = useNavigate();
  const fr = lang === 'fr';
  const lastActivity = useRef(Date.now());
  const lastHeartbeat = useRef(Date.now());
  const warned = useRef(false);
  const [secondsLeft, setSecondsLeft] = useState(null);

  const expire = useCallback(async () => {
    warned.current = false;
    setSecondsLeft(null);
    await logout();
    toast.warning(fr ? 'Session expirée après 30 minutes d’inactivité.' : 'Session expired after 30 minutes of inactivity.', { duration: 8000 });
    navigate('/login', { replace: true, state: { reason: 'idle' } });
  }, [logout, navigate, fr]);

  const stayConnected = useCallback(async () => {
    lastActivity.current = Date.now();
    lastHeartbeat.current = Date.now();
    warned.current = false;
    setSecondsLeft(null);
    try { await axios.post(`${API}/auth/refresh`, {}); } catch { expire(); }
  }, [expire]);

  useEffect(() => {
    const onActivity = () => { if (!warned.current) lastActivity.current = Date.now(); };
    EVENTS.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    const id = setInterval(() => {
      const idle = Date.now() - lastActivity.current;
      if (idle >= IDLE_MS) { expire(); return; }
      if (idle >= IDLE_MS - WARN_MS) {
        warned.current = true;
        setSecondsLeft(Math.ceil((IDLE_MS - idle) / 1000));
      } else if (idle < HEARTBEAT_MS && Date.now() - lastHeartbeat.current >= HEARTBEAT_MS) {
        lastHeartbeat.current = Date.now();
        axios.get(`${API}/auth/me`).catch(() => {});
      }
    }, 1000);
    return () => { clearInterval(id); EVENTS.forEach((e) => window.removeEventListener(e, onActivity)); };
  }, [expire]);

  const open = secondsLeft !== null;
  const mm = open ? String(Math.floor(secondsLeft / 60)).padStart(2, '0') : '00';
  const ss = open ? String(secondsLeft % 60).padStart(2, '0') : '00';

  return (
    <Dialog open={open}>
      <DialogContent className="bg-[#0C1E27] border-[#F5C544]/40 max-w-md [&>button]:hidden" data-testid="idle-warning-dialog">
        <DialogTitle className="text-white flex items-center gap-2"><TimerReset className="w-5 h-5 text-[#F5C544]" /> {fr ? 'Session inactive' : 'Inactive session'}</DialogTitle>
        <DialogDescription className="text-slate-400">
          {fr ? 'Pour votre sécurité, vous serez déconnecté automatiquement dans' : 'For your security, you will be signed out automatically in'}
        </DialogDescription>
        <div className="font-mono text-5xl font-bold text-[#F5C544] text-center tabular-nums py-2" data-testid="idle-countdown">{mm}:{ss}</div>
        <div className="flex gap-2 justify-end">
          <Button variant="outline" onClick={expire} className="border-[#285D71] text-slate-300 hover:bg-[#1E5164]" data-testid="idle-logout-btn"><LogOut className="w-4 h-4 mr-2" /> {fr ? 'Se déconnecter' : 'Sign out'}</Button>
          <Button onClick={stayConnected} className="bg-[#45B08F] hover:bg-[#3A9679] text-[#112C38] font-semibold" data-testid="idle-stay-btn">{fr ? 'Rester connecté' : 'Stay signed in'}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
